import React from "react";
import type { Position } from "@foundation/watchlist-client";
import styles from "./styles.module.css";
import {
  PortfolioColumnConfig,
  PortfolioLot,
  PortfolioSortState,
} from "~types/portfolio";
import SortableHeader from "~components/Portfolio/PortfolioTable/SortableHeader";
import PortfolioRow from "~components/Portfolio/PortfolioTable/PortfolioRow";
import { ROW_TYPES } from "~constants/Portfolio";

const { TICKER, LOT } = ROW_TYPES;

type DataGridProps = {
  columnsConfig: Array<PortfolioColumnConfig>;
  cycleSortState: (column: string) => void;
  sortState: PortfolioSortState;
  watchlistRows: Array<Position | PortfolioLot>;
};

function generateSortableHeaders(
  columnsConfig: Array<PortfolioColumnConfig>,
  sortState: PortfolioSortState,
  cycleSortState: (column: string) => void
) {
  return columnsConfig.map((columnConfig) => {
    const { columnName, alignment, columnAriaDescription, enableSort } =
      columnConfig;

    return (
      <SortableHeader
        key={`${columnName}-header`}
        header={columnName}
        alignment={alignment}
        columnAriaDescription={columnAriaDescription}
        sortState={sortState}
        cycleSortState={cycleSortState}
        enableSort={enableSort}
      />
    );
  });
}

function generateRows(
  watchlistRows: Array<Position | PortfolioLot>,
  columnsConfig: Array<PortfolioColumnConfig>
) {
  return watchlistRows.map((watchlistRow) => {
    let rowType;
    let key;

    if ((watchlistRow as PortfolioLot).lotId) {
      rowType = LOT;
      key = `${(watchlistRow as PortfolioLot).ticker}-${
        (watchlistRow as PortfolioLot).lotId
      }-row`;
    } else {
      rowType = TICKER;
      key = `${(watchlistRow as Position).security.ticker}-row`;
    }

    return (
      <PortfolioRow
        key={key}
        watchlistRow={watchlistRow}
        columnsConfig={columnsConfig}
        rowType={rowType}
      />
    );
  });
}

export default function DataTable(props: DataGridProps) {
  const { columnsConfig, sortState, cycleSortState, watchlistRows } = props;

  return (
    <div className={styles.dataTable}>
      <div className={styles.scrollableContainer} role="table">
        <div className={styles.sortableHeaders} role="rowgroup">
          <div role="row">
            {generateSortableHeaders(columnsConfig, sortState, cycleSortState)}
          </div>
        </div>
        <div className={styles.tableEntries} role="rowgroup">
          {generateRows(watchlistRows, columnsConfig)}
        </div>
      </div>
    </div>
  );
}
